import styled, { DefaultTheme, useTheme } from "styled-components";
import { GradientButton } from "../../styles/common/Button.styled";
import * as S from "../../styles/layout/Area.styled";
import * as S2 from "../../styles/layout/MainLayout.styled";
import LabelInput from "../common/LabelInput";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "../../hooks/useApi";
import toast from "react-hot-toast";
import { LoadingIcon } from "../common/Icons";
import { useAuth } from "../../contexts/AuthContext";
import { basicApi, coreApi } from "../../utils/api";

type LoginMode = 'basic' | 'core';

interface ModuleInfo {
    routingKey?: string;
    subModuleMode?: string;
}

const GROUP_NAME_STORAGE_KEY = 'callis.login.groupName';
const LOGIN_ID_STORAGE_KEY = 'callis.login.loginId';

const loadSavedLoginData = () => ({
    groupName: localStorage.getItem(GROUP_NAME_STORAGE_KEY) || '',
    loginId: localStorage.getItem(LOGIN_ID_STORAGE_KEY) || '',
    password: '',
});

const getLoginModeColor = (mode: LoginMode | null, theme: DefaultTheme) => (
    mode === 'basic'
        ? theme.name === 'dark' ? '#34D399' : '#167A5B'
        : theme.color.point_y_100
);

const LoginFormContent = styled(S2.Padding)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: space-between;
`;

const LoginFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: flex-start;
`;

const GroupStatus = styled.div<{ $mode?: LoginMode }>`
    position: absolute;
    right: 0;
    bottom: 5px;
    display: flex;
    align-items: center;
    min-height: 28px;
    white-space: nowrap;
`;

const GroupModeDot = styled.span<{ $mode: LoginMode }>`
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $mode, theme }) => getLoginModeColor($mode, theme)};
    box-shadow: 0 0 0 3px ${({ $mode, theme }) => $mode === 'basic'
        ? theme.name === 'dark' ? 'rgba(52, 211, 153, 0.16)' : 'rgba(22, 122, 91, 0.12)'
        : theme.name === 'dark' ? 'rgba(109, 141, 255, 0.18)' : 'rgba(61, 89, 182, 0.12)'};
`;

const LoginForm = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { loading: basicLoading, request: basicRequest } = useApi(basicApi);
    const { loading: coreLoading, request: coreRequest } = useApi(coreApi);
    const loading = basicLoading || coreLoading;
    const { login } = useAuth();
    const [formData, setFormData] = useState(loadSavedLoginData);
    const [detectedMode, setDetectedMode] = useState<LoginMode | null>(null);
    const [isCheckingGroupModule, setIsCheckingGroupModule] = useState(false);
    const groupNameInputRef = useRef<HTMLInputElement>(null);
    const loginIdInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const hasFocusedInitialInput = useRef(false);
    const groupModuleCheckVersion = useRef(0);
    const lastCheckedGroup = useRef<string | null>(null);

    const detectedModeColor = getLoginModeColor(detectedMode, theme);
    const buttonTextColor = theme.name === 'dark' ? theme.color.primary_200 : '#FFFFFF';

    useEffect(() => {
        if (hasFocusedInitialInput.current) return;
        hasFocusedInitialInput.current = true;

        [
            { value: formData.groupName, inputRef: groupNameInputRef },
            { value: formData.loginId, inputRef: loginIdInputRef },
            { value: formData.password, inputRef: passwordInputRef },
        ].find(({ value, inputRef }) => {
            if (value) return false;
            inputRef.current?.focus();
            return true;
        });
    }, [formData.groupName, formData.loginId, formData.password]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'groupName') {
            groupModuleCheckVersion.current += 1;
            lastCheckedGroup.current = null;
            setDetectedMode(null);
            setIsCheckingGroupModule(false);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const requestModuleInfo = useCallback((groupCode: string) => coreRequest({
        url: '/module-info',
        method: 'GET',
        params: { groupCode },
    }) as Promise<ModuleInfo>, [coreRequest]);

    const checkGroupModule = useCallback(async (groupName: string): Promise<ModuleInfo | null> => {
        const version = ++groupModuleCheckVersion.current;
        const groupCode = groupName.trim();
        lastCheckedGroup.current = null;

        if (!groupCode) {
            setDetectedMode(null);
            setIsCheckingGroupModule(false);
            return null;
        }

        setIsCheckingGroupModule(true);

        try {
            const moduleInfo = await requestModuleInfo(groupCode);
            if (version === groupModuleCheckVersion.current) {
                const mode: LoginMode = moduleInfo?.subModuleMode === 'CALLIS_BASIC' ? 'basic' : 'core';
                setDetectedMode(mode);
                lastCheckedGroup.current = groupCode;
            }
            return moduleInfo || null;
        } catch (error: any) {
            if (version === groupModuleCheckVersion.current) {
                setDetectedMode(error.response?.status === 404 ? 'core' : null);
                if (error.response?.status === 404) lastCheckedGroup.current = groupCode;
            }
            if (error.response?.status === 404) return null;
            throw error;
        } finally {
            if (version === groupModuleCheckVersion.current) setIsCheckingGroupModule(false);
        }
    }, [requestModuleInfo]);

    const handleGroupNameBlur = () => {
        const groupCode = formData.groupName.trim();
        if (!groupCode || lastCheckedGroup.current === groupCode || isCheckingGroupModule) return;
        void checkGroupModule(formData.groupName).catch(() => {
            toast.error('그룹 정보를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.');
        });
    };

    useEffect(() => {
        const savedGroupName = groupNameInputRef.current?.value.trim();
        if (savedGroupName) void checkGroupModule(savedGroupName).catch(() => undefined);
    }, [checkGroupModule]);

    const handleLogin = async () => {
        localStorage.setItem(GROUP_NAME_STORAGE_KEY, formData.groupName);
        localStorage.setItem(LOGIN_ID_STORAGE_KEY, formData.loginId);

        if (!formData.groupName || !formData.loginId || !formData.password) {
            toast.error("모든 필드를 입력해주세요.");
            return;
        }

        let moduleInfo: ModuleInfo | null;
        try {
            moduleInfo = await checkGroupModule(formData.groupName);
        } catch (error) {
            console.error("Module info lookup failed:", error);
            toast.error('그룹 정보를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.');
            return;
        }

        const loginData = {
            ...formData,
            platform: 'admin'
        };

        try {
            if (moduleInfo?.subModuleMode !== 'CALLIS_BASIC') {
                const response = await coreRequest({
                    url: '/user/login',
                    method: 'POST',
                    data: loginData
                });
                const { accessToken, ...userData } = response || {};

                if (!accessToken) {
                    toast.error("로그인 정보가 올바르지 않습니다. (토큰 누락)");
                    return;
                }

                login(userData as any, accessToken, {
                    mode: 'core',
                    coreUser: userData,
                    coreAccessToken: accessToken,
                });
                toast.success("반갑습니다! 로그인에 성공했습니다.");
                navigate("/statistics/calls");
                return;
            }

            if (!moduleInfo.routingKey) {
                toast.error("그룹의 라우팅 키를 조회하지 못했습니다.");
                return;
            }

            const basicResponse = await basicRequest({
                url: '/user/login',
                method: 'POST',
                data: loginData
            });
            const { accessToken: basicAccessToken, ...basicUserData } = basicResponse || {};

            if (!basicAccessToken) {
                toast.error("로그인 정보가 올바르지 않습니다. (토큰 누락)");
                return;
            }

            const coreResponse = await coreRequest({
                url: '/user/login/third-party',
                method: 'POST',
                data: {
                    routingKey: moduleInfo.routingKey,
                    sessionToken: basicAccessToken,
                    platform: 'admin',
                },
            });
            const { accessToken: coreAccessToken, ...coreUserData } = coreResponse || {};

            if (!coreAccessToken) {
                toast.error("로그인 정보가 올바르지 않습니다. (토큰 누락)");
                return;
            }

            login(basicUserData as any, basicAccessToken, {
                mode: 'basic',
                basicUser: basicUserData,
                basicAccessToken,
                coreUser: coreUserData,
                coreAccessToken,
            });
            toast.success("반갑습니다! 로그인에 성공했습니다.");
            navigate("/statistics/calls");
        } catch (error: any) {
            console.error("Login failed:", error);
            const errorMessage = error.response?.data?.message || "로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.";
            toast.error(errorMessage);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') void handleLogin();
    };

    return (
        <S.Area $width={600} $height={350}>
            <LoginFormContent $size1={40} $size2={45}>
                <LoginFields onKeyDown={handleKeyDown}>
                    <LabelInput
                        name="groupName"
                        label="그룹 아이디"
                        placeholder="그룹 아이디를 입력해주세요"
                        value={formData.groupName}
                        focusColor={detectedModeColor}
                        inputRef={groupNameInputRef}
                        autoComplete="off"
                        preventAutofill
                        onBlur={handleGroupNameBlur}
                        trailingContent={isCheckingGroupModule ? (
                            <GroupStatus role="status" aria-label="그룹 정보 확인 중">
                                <LoadingIcon size={14} color={detectedModeColor} />
                            </GroupStatus>
                        ) : detectedMode ? (
                            <GroupStatus
                                role="status"
                                $mode={detectedMode}
                                aria-label={`${detectedMode === 'basic' ? '베이직' : '코어'} 로그인 그룹`}
                            >
                                <GroupModeDot data-testid="group-mode-dot" $mode={detectedMode} aria-hidden="true" />
                            </GroupStatus>
                        ) : undefined}
                        onChange={handleChange}
                    />
                    <LabelInput
                        name="loginId"
                        label="로그인 아이디"
                        placeholder="로그인 아이디를 입력해주세요"
                        value={formData.loginId}
                        focusColor={detectedModeColor}
                        inputRef={loginIdInputRef}
                        autoComplete="off"
                        preventAutofill
                        onChange={handleChange}
                    />
                    <LabelInput
                        name="password"
                        type="password"
                        label="비밀번호"
                        placeholder="비밀번호를 입력해주세요"
                        value={formData.password}
                        focusColor={detectedModeColor}
                        inputRef={passwordInputRef}
                        autoComplete="current-password"
                        showPasswordToggle
                        onChange={handleChange}
                    />
                    <GradientButton
                        color1={theme.color.point_y_100}
                        color2={theme.color.point_y_200}
                        color3={theme.color.point_y_300}
                        style={{
                            flexShrink: 0,
                            backgroundColor: detectedModeColor,
                            borderColor: detectedModeColor,
                            color: buttonTextColor,
                        }}
                        onClick={handleLogin}
                        disabled={loading || isCheckingGroupModule}
                    >
                        {loading || isCheckingGroupModule ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <LoadingIcon color={buttonTextColor} />
                                <span>{isCheckingGroupModule ? '그룹 확인 중...' : '로그인 중...'}</span>
                            </div>
                        ) : '콜리스 로그인'}
                    </GradientButton>
                </LoginFields>
            </LoginFormContent>
        </S.Area>
    )
}

export default LoginForm;
